import React from 'react'
import { Box, Button, ButtonBase } from '@mui/material'

import { AppEmptyLine } from '@components'
import { dsColorNew } from '@palette'
import { fmtDay, fromIso } from '../../../@services/dates'
import { pct } from '../@services/colourScale'
import { findRows } from '../@services/groups'
import DistanceCell from './DistanceCell'


export const gridSx = {
	wrap: { overflow: 'auto', maxHeight: 'calc(100vh - 210px)', border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', position: 'relative' },
	table: {
		borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, minWidth: '100%',
		'& th': {
			position: 'sticky', top: 0, zIndex: 3, backgroundColor: dsColorNew.tableHead, color: 'rgba(255,255,255,.92)',
			fontWeight: 700, fontSize: 13, px: 0.75, py: 1, textAlign: 'center', whiteSpace: 'nowrap', border: 0, lineHeight: 1.25,
		},
		'& th small': { display: 'block', fontWeight: 500, fontSize: 11, color: 'rgba(255,255,255,.62)' },
		'& th.rowh': { left: 0, zIndex: 5, textAlign: 'left', pl: 2, minWidth: 210 },
		'& td': { borderBottom: `1px solid ${dsColorNew.grayBorder}`, p: '3px', backgroundColor: dsColorNew.white, textAlign: 'center', verticalAlign: 'middle' },
		'& td.rowh': { position: 'sticky', left: 0, zIndex: 2, textAlign: 'left', p: '6px 12px 6px 16px', whiteSpace: 'nowrap', borderRight: `1px solid ${dsColorNew.grayBorder}`, minWidth: 210 },
		'& tr.office:hover td': { backgroundColor: dsColorNew.hoverBgLight },
		'& tr.group td': { backgroundColor: '#F6F7F9', fontSize: 12, fontWeight: 600, color: dsColorNew.textDark },
		'& tr.empty td.note': { textAlign: 'left', fontSize: 12, color: dsColorNew.textMuted, px: 2 },
	},
	sortBtn: { color: 'inherit', font: 'inherit', fontWeight: 700, borderRadius: '6px', px: 0.5, display: 'inline-flex', flexDirection: 'column', alignItems: 'center' },
}

const TYPE = { airport: 'Airport', downtown: 'City', train_station: 'Train station', truck: 'Trucks', other: 'Other' }

export function OfficeName({ office }) {
	return <Box>
		<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{office.name}</Box>
		<Box component="span" sx={{ display: 'block', fontSize: 11.5, color: dsColorNew.textMuted }}>{[office.countryName, TYPE[office.type]].filter(Boolean).join(' · ')}</Box>
	</Box>
}

/** The line of an office with no prices on the capture days, or without a website of yours there (CA 9, 12) */
export function RowNote({ row, captureText, onLast7, onSettings }) {
	if (row.reason === 'notCaptured') {
		const last = row.office.lastCapture ? fromIso(row.office.lastCapture) : null
		return <>No prices captured on {captureText}{last ? ` · last capture ${fmtDay(last)}` : ''}{onLast7 && <> · <Button size="small" variant="text" onClick={onLast7} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: 12 }}>Use the last 7 days</Button></>}</>
	}
	return <>No website of yours in {row.office.countryName || row.office.country} · <Button size="small" variant="text" onClick={onSettings} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: 12 }}>Add it in Settings › Account</Button></>
}

function SortHeader({ label, sub, sortKey, sort, onSort, today }) {
	const on = sort?.key === sortKey
	return <th className={today ? 'today' : undefined} aria-sort={on ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
		<ButtonBase onClick={() => onSort(sortKey)} sx={gridSx.sortBtn} title="Sort by this column: most above first, click again for most below">
			<span>{label}{on ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}</span>
			{!!sub && <small>{sub}</small>}
		</ButtonBase>
	</th>
}

/**
 * The heat map of the lengths view and of the pick-up view (CA 14, 16, 19): one row per office, grouped and foldable,
 * a summary row per group (median of the distances, N of M above), sortable headers, fixed header and office column.
 */
export default function NetworkGrid({ model, groups, mode, code, sort, onSort, search, compact, showChange, perDay, captureText, onToggleGroup, onOpen, onLast7, onSettings }) {
	const columns = model.columns
	return <Box sx={gridSx.wrap}>
		<Box component="table" sx={gridSx.table}>
			<thead><tr>
				<SortHeader label="Office" sortKey="name" sort={sort} onSort={onSort}/>
				{columns.map((c, j) => <SortHeader key={c.key} label={c.label} sub={c.sub && c.sub !== c.label ? c.sub : null} sortKey={`col:${j}`} sort={sort} onSort={onSort}/>)}
			</tr></thead>
			<tbody>
				{groups.map(g => {
					const rows = findRows(g.rows, search)
					if (search && !rows.length) return null
					return <React.Fragment key={g.key}>
						{g.label !== null && <tr className="group">
							<td className="rowh">
								<ButtonBase aria-expanded={!g.collapsed} onClick={() => onToggleGroup(g.key)} sx={{ font: 'inherit', fontWeight: 700, gap: 0.75 }}>
									{g.collapsed ? '▸' : '▾'} {g.label}
								</ButtonBase>
								<Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: 11.5, color: dsColorNew.textMuted }}>
									{g.rows.length} office{g.rows.length === 1 ? '' : 's'}{g.notCaptured ? ` · ${g.notCaptured} not captured` : ''}
								</Box>
							</td>
							{g.summary.map((s, j) => <td key={j} title={s.withData ? `Median of the distances of the ${s.withData} offices with data · ${s.above} of ${s.withData} above the market` : 'No office with data'}>
								{s.withData ? <>
									<Box component="span" sx={{ display: 'block' }}>{mode === 'market' ? pct(s.median) : `${s.median >= 0.02 ? '▲' : s.median <= -0.02 ? '▼' : '≈'} ${pct(s.median)}`}</Box>
									<Box component="span" sx={{ fontWeight: 400, fontSize: 11, color: dsColorNew.textMuted }}>{mode === 'market' ? 'median change' : compact ? `${s.above}/${s.withData} ▲` : `${s.above} of ${s.withData} above`}</Box>
								</> : '—'}
							</td>)}
						</tr>}
						{!g.collapsed && rows.map(row => (row.reason
							? <tr key={row.office.id} className="office empty">
								<td className="rowh"><OfficeName office={row.office}/></td>
								<td className="note" colSpan={columns.length}><RowNote row={row} captureText={captureText} onLast7={onLast7} onSettings={onSettings}/></td>
							</tr>
							: <tr key={row.office.id} className="office">
								<td className="rowh"><OfficeName office={row.office}/></td>
								{row.cells.map((cell, j) => <td key={columns[j].key}>
									<DistanceCell cell={cell} mode={mode} code={code} compact={compact} showChange={showChange} perDay={perDay}
										aria={`${row.office.name}, ${columns[j].label}`} onOpen={() => onOpen(row.office.id, j)}/>
								</td>)}
							</tr>))}
					</React.Fragment>
				})}
			</tbody>
		</Box>
		{!!search && groups.every(g => !findRows(g.rows, search).length) && <AppEmptyLine>No office matches “{search}”.</AppEmptyLine>}
	</Box>
}
